import * as React from "react"
import { useState, useRef, useEffect } from "react"
import { Clock } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

// Generate hours (00-23)
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))

// Generate minutes (00-59) in 5-minute increments
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'))

// Scrollable column component
const ScrollColumn = ({ items, selected, onSelect, label }) => {
  const scrollRef = useRef(null)

  useEffect(() => {
    if (scrollRef.current) {
      const index = items.indexOf(selected)
      if (index > 0) {
        scrollRef.current.scrollTop = Math.max(0, (index - 2) * 36)
      }
    }
  }, [selected, items])

  return (
    <div className="flex-1 flex flex-col">
      <div className="py-2 text-[11px] text-zinc-500 font-medium border-b border-white/10 text-center uppercase tracking-wider">
        {label}
      </div>
      <div 
        ref={scrollRef}
        className="scroll-container"
        style={{
          height: '180px',
          overflowY: 'scroll',
          overscrollBehavior: 'contain',
        }}
        onWheel={(e) => {
          // Stop propagation to prevent popover from intercepting
          e.stopPropagation()
        }}
      >
        {items.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onSelect(item)}
            className={cn(
              "w-full h-9 text-sm text-center",
              selected === item
                ? "bg-white text-zinc-900 font-medium"
                : "text-zinc-300 hover:bg-white/10"
            )}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  )
}

function TimePicker({
  value,
  onChange,
  className,
  placeholder = "Select time",
}) {
  const [open, setOpen] = useState(false)
  const [selectedHour, setSelectedHour] = useState(() => {
    if (value) {
      const [h] = value.split(':')
      return h
    }
    return '12'
  })
  const [selectedMinute, setSelectedMinute] = useState(() => {
    if (value) {
      const [, m] = value.split(':')
      const mins = parseInt(m)
      const rounded = Math.round(mins / 5) * 5
      return String(rounded === 60 ? 0 : rounded).padStart(2, '0')
    }
    return '00'
  })

  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':')
      setSelectedHour(h)
      const mins = parseInt(m)
      const rounded = Math.round(mins / 5) * 5
      setSelectedMinute(String(rounded === 60 ? 0 : rounded).padStart(2, '0'))
    }
  }, [value])

  const handleHourSelect = (hour) => {
    setSelectedHour(hour)
    onChange?.(`${hour}:${selectedMinute}`)
  }

  const handleMinuteSelect = (minute) => {
    setSelectedMinute(minute)
    onChange?.(`${selectedHour}:${minute}`)
  }

  const displayValue = value || `${selectedHour}:${selectedMinute}`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal bg-[#111318] border-white/10 text-zinc-300 hover:bg-[#111318] hover:text-zinc-100",
            !value && "text-zinc-500",
            className
          )}
        >
          <Clock size={14} className="mr-2 text-zinc-500" />
          {displayValue}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[160px] p-0 bg-[#0a0a0a] border-white/10 shadow-xl" 
        align="start"
        sideOffset={8}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <style>{`
          .scroll-container::-webkit-scrollbar {
            width: 4px;
          }
          .scroll-container::-webkit-scrollbar-track {
            background: transparent;
          }
          .scroll-container::-webkit-scrollbar-thumb {
            background: #3f3f46;
            border-radius: 2px;
          }
          .scroll-container::-webkit-scrollbar-thumb:hover {
            background: #52525b;
          }
        `}</style>
        <div className="flex">
          <ScrollColumn 
            items={HOURS} 
            selected={selectedHour} 
            onSelect={handleHourSelect}
            label="Hr"
          />
          <div className="w-px bg-white/10" />
          <ScrollColumn 
            items={MINUTES} 
            selected={selectedMinute} 
            onSelect={handleMinuteSelect}
            label="Min"
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}

TimePicker.displayName = "TimePicker"

export { TimePicker }
